import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './VideoFrame.css';
import movie from "./clip/presentation_prophecyxr_v2.mp4";

function VideoFrame() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showThumbnail, setShowThumbnail] = useState(true);
  const videoRef = useRef(null);
  const buttonTimeoutRef = useRef(null);
  const navigate = useNavigate();
  
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
        setShowControls(true);
        if (buttonTimeoutRef.current) {
          clearTimeout(buttonTimeoutRef.current);
        }
      } else {
        videoRef.current.play();
        setShowThumbnail(false); // Hide thumbnail when playing
        // Hide the button after 1 second of playing
        buttonTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 1000);
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Show controls when hovering the video
  const handleVideoHover = () => {
    if (isPlaying) {
      setShowControls(true);
    }
  };

  // Hide controls when leaving the video
  const handleVideoLeave = () => {
    if (isPlaying) {
      setShowControls(false);
    }
  };

  const handleBackToHome = () => {
    // Reset scroll and body styles for clean navigation
    window.scrollTo(0, 0);
    
    // Create a minimal delay to ensure browser has time to process style changes
    setTimeout(() => {
      navigate('/', { replace: true });
    }, 50);
  };

  // Handle video loading and set initial position
  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (videoElement) {
      // Set up event listeners for loading
      const handleCanPlay = () => {
        setIsLoaded(true);
      };
      
      videoElement.addEventListener('canplay', handleCanPlay);
      
      // Clean up listener
      return () => {
        videoElement.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, []);

  useEffect(() => {
    // Add event listeners to handle video state
    const videoElement = videoRef.current;
    
    if (videoElement) {
      const handleEnded = () => {
        setIsPlaying(false);
        setShowControls(true);
        setShowThumbnail(true); // Show thumbnail when video ends
      };
      
      const handlePause = () => {
        setIsPlaying(false);
        setShowControls(true);
        if (buttonTimeoutRef.current) {
          clearTimeout(buttonTimeoutRef.current);
        }
      };
      
      const handlePlay = () => {
        setIsPlaying(true);
        setShowThumbnail(false); // Hide thumbnail when playing
        // Hide the button after 1 second of playing
        buttonTimeoutRef.current = setTimeout(() => {
          setShowControls(false);
        }, 1000);
      };
      
      videoElement.addEventListener('ended', handleEnded);
      videoElement.addEventListener('pause', handlePause);
      videoElement.addEventListener('play', handlePlay);
      
      return () => {
        videoElement.removeEventListener('ended', handleEnded);
        videoElement.removeEventListener('pause', handlePause);
        videoElement.removeEventListener('play', handlePlay);
        
        // Clean up the timeout when component unmounts
        if (buttonTimeoutRef.current) {
          clearTimeout(buttonTimeoutRef.current);
        }
      };
    }
  }, []);

  return (
    <div className="video-iphone-page">
      <div className="header-title">
        <h1>Prophecy</h1>
        <div className="xr-box">XR</div>
      </div>
      
      <div className="catch-phrase">
        Proposez à vos clients en un clique, l'accès à l'ensemble de vos oeuvres d'art
      </div>
      <div className="ar-text">
        <p>Prophecy se porte garant de</p>
        <div className="text-column">
          <p className="column-item">Simplicité</p>
          <p className="column-item">Efficacité</p>
          <p className="column-item">Accessibilité</p>
          <p className="column-item">Durabilité</p>
        </div>
      </div>
      <div className="iphone-container">
        {/* iPhone frame - this is a static image of an iPhone */}
        <div className="iphone-frame">
          {/* Video container - positioned exactly over the iPhone screen area */}
          <div 
            className="iphone-screen"
            onMouseEnter={handleVideoHover}
            onMouseLeave={handleVideoLeave}
          >
            {/* Custom thumbnail overlay */}
            {showThumbnail && !isPlaying && (
              <div className="video-thumbnail">
                <div className="thumbnail-content">
                  <div className="thumbnail-logo">
                    <h2 className="prophecy-title">Prophecy<span className="thumbnail-xr">XR</span></h2>
                  </div>
                  <p className="thumbnail-tagline">Démonstration d'un placement d'une oeuvre digitalisée accessible prochainement sur votre site internet</p>
                </div>
              </div>
            )}
            
            <video 
              ref={videoRef}
              className="screen-video"
              loop
              playsInline
              preload="auto"
              poster={!isLoaded ? "" : undefined}
              src={movie}
            >
              Votre navigateur ne supporte pas la lecture de vidéos.
            </video>
            
            {/* Play/Pause Button overlay */}
            <button 
              className={`video-control ${!showControls ? 'hidden' : ''}`} 
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              {isPlaying ? (
                <i className="bi bi-pause-fill"></i>
              ) : (
                <i className="bi bi-play-fill"></i>
              )}
            </button>
          </div>
        </div>
      </div>
      <div className="ar-cta">
        <button onClick={handleBackToHome} className="back-button">
          Retour à la page d'accueil
        </button>
      </div>
    </div>
  );
}

export default VideoFrame;